import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../hooks/useLanguage';
import { CornerBrackets } from './CornerBrackets';
import { CyberButton } from './CyberButton';
import { ChevronDown } from 'lucide-react';
import { ProjectModal } from './ProjectModal';
import { ContactModal } from './ContactModal';
interface HeroProps {
  onModalStateChange?: (isOpen: boolean) => void;
}
export function Hero({
  onModalStateChange
}: HeroProps) {
  const {
    t,
    language
  } = useLanguage();
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(isProjectModalOpen || isContactModalOpen);
    }
  }, [isProjectModalOpen, isContactModalOpen, onModalStateChange]);
  const handleScrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  // Hero title based on language - 3 lines for both
  const heroTitle = language === 'fr' ? {
    line1: 'SOLUTIONS',
    line2: 'DIGITALES',
    line3: '& IA'
  } : {
    line1: 'AI-POWERED',
    line2: 'DIGITAL',
    line3: 'SOLUTIONS'
  };
  return <section className="relative min-h-screen flex items-center justify-center pt-16 sm:pt-20 px-4 overflow-hidden" data-id="element-169">
      {/* Custom Glitch Styles */}
      <style data-id="element-170">{`
        @keyframes glitch-anim {
          0% {
            text-shadow: 0 0 0 transparent;
          }
          2% {
            text-shadow: 2px 0 0 rgba(255, 0, 255, 0.7), -2px 0 0 rgba(0, 212, 255, 0.7);
          }
          4% {
            text-shadow: -2px 0 0 rgba(255, 0, 255, 0.7), 2px 0 0 rgba(0, 212, 255, 0.7);
          }
          6% {
            text-shadow: 0 0 0 transparent;
          }
          100% {
            text-shadow: 0 0 0 transparent;
          }
        }
        .glitch-container {
          animation: glitch-anim 5s infinite linear;
        }
        
        @keyframes scroll-bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(8px);
          }
        }
        
        .scroll-indicator {
          animation: scroll-bounce 2s ease-in-out infinite;
        }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto text-center" data-id="element-171">
        {/* Status Badge */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1 sm:py-1.5 mb-6 sm:mb-10 border border-cyan/30 bg-cyan/5 backdrop-blur-sm" style={{
        clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)'
      }} data-id="element-172">
          <div className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2" data-id="element-173">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan opacity-75" data-id="element-174"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-cyan" data-id="element-175"></span>
          </div>
          <span className="text-[9px] sm:text-xs font-cyber text-cyan tracking-[0.15em] sm:tracking-[0.2em]" data-id="element-176">
            {t.hero.systemStatus}
          </span>
        </motion.div>

        {/* Main Title - 3 lines for both languages */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="glitch-container mb-4 sm:mb-8" data-id="element-177">
          <h1 className="font-cyber text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-none tracking-wide" data-id="element-178">
            <span className="block text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]" data-id="element-179">
              {heroTitle.line1}
            </span>
            <span className="block text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)] mt-1 sm:mt-2" data-id="element-180">
              {heroTitle.line2}
            </span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400 mt-1 sm:mt-2" data-id="element-181">
              {heroTitle.line3}
            </span>
          </h1>
        </motion.div>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="max-w-2xl mx-auto text-gray-400 text-sm sm:text-base md:text-lg mb-8 sm:mb-12 font-light leading-relaxed px-4" data-id="element-182">
          {t.hero.subtitle}
        </motion.p>

        {/* Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 px-4" data-id="element-183">
          <CyberButton variant="primary" onClick={() => setIsProjectModalOpen(true)} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm" data-id="element-184">
            {t.hero.ctaPrimary}
          </CyberButton>
          <CyberButton variant="secondary" onClick={handleScrollToServices} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm" data-id="element-185">
            {t.hero.ctaSecondary}
          </CyberButton>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1
    }} className="absolute bottom-8 sm:bottom-12 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 cursor-pointer group" onClick={() => {
      window.scrollTo({
        top: window.innerHeight,
        behavior: 'smooth'
      });
    }} data-id="element-186">
        <span className="text-[10px] sm:text-xs font-cyber text-gray-500 uppercase tracking-widest group-hover:text-cyan transition-colors" data-id="element-187">
          {t.hero.scroll}
        </span>
        <div className="scroll-indicator flex flex-col items-center" data-id="element-188">
          <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-cyan opacity-60 group-hover:opacity-100 transition-opacity" data-id="element-189" />
          <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-cyan opacity-30 -mt-2 sm:-mt-3" data-id="element-190" />
        </div>
      </motion.div>

      {/* Decorative Grid Lines */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,212,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,212,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px] sm:bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_70%)] pointer-events-none" data-id="element-191" />

      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" data-id="element-192" />

      {/* Modals */}
      <ProjectModal isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)} data-id="element-193" />
      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} data-id="element-194" />
    </section>;
}